import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Tabs,
    Tab,
    Box,
    Grid,
    Button,
    Avatar,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    Checkbox,
    ImageList,
    ImageListItem,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Chip,
    Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Settings,
    Twitter,
    Instagram,
    Github,
    Link2,
    Heart,
    ExternalLink,
    MapPin,
    Calendar,
    Users,
    Image as ImageIcon,
    MessageCircle,
    Palette,
    BookOpen,
    Star,
    Users2,
    Crown,
    QrCode,
    ArrowForwardIos as ArrowForwardIosIcon
} from 'lucide-react';

// Heroセクション用のスタイル
const HeroContainer = styled('div')(({ theme }) => ({
    position: 'relative',
    height: '500px',
    overflow: 'hidden',
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.8) 100%)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: 'url(/api/placeholder/1920/1080)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        zIndex: -1,
    },
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: theme.spacing(20),
    [theme.breakpoints.down('md')]: {
        height: 'auto',
        paddingBottom: theme.spacing(8),
    },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(16),
    height: theme.spacing(16),
    border: `4px solid ${theme.palette.common.white}`,
    boxShadow: theme.shadows[8],
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'scale(1.05)',
    },
    '& .MuiAvatar-img': {
        objectFit: 'cover',
    },
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: theme.spacing(2),
        height: theme.spacing(2),
        borderRadius: '50%',
        backgroundColor: theme.palette.success.main,
        border: `4px solid ${theme.palette.common.white}`,
    }
}));

const ActionButtons = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        marginTop: theme.spacing(2),
    }
}));


const TabPanel = (props) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`user-profile-tabpanel-${index}`}
            aria-labelledby={`user-profile-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `user-profile-tab-${index}`,
        'aria-controls': `user-profile-tabpanel-${index}`,
    };
}


const UserProfile2 = ({ userInfo }) => {
    const [value, setValue] = useState(0);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            background: 'linear-gradient(to bottom right, #f0f0f0, #f5f5f5)',
        }}>
            {/* ヒーローセクション */}
            <HeroContainer>
                <Box container sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    height: '100%',
                    padding: (theme) => theme.spacing(0, 4),
                    width: { xs: '100%', md: 'calc(100% - 40px)' },
                    maxWidth: '1280px',
                    margin: '0 auto',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 8, mb: { xs: 2, md: 0 }, flexDirection: { xs: 'column', md: 'row' } }}>
                        {/* プロフィール画像 */}
                        <ProfileAvatar src="/api/placeholder/400/400" alt={userInfo?.name} />

                        {/* プロフィール情報 */}
                        <Box sx={{ flex: 1, color: 'white' }}>
                            <Typography variant="h4" fontWeight="bold" mb={1}>{userInfo?.name || 'クリエイター'}</Typography>
                            <Typography variant="body1" color="grey.300" mb={3} maxWidth={800}>
                                {userInfo?.bio || 'アートとテクノロジーを組み合わせて、ユニークな作品を制作しています。'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MapPin size={16} />
                                    <Typography variant="body2">東京</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Calendar size={16} />
                                    <Typography variant="body2">2024年から活動中</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Users size={16} />
                                    <Typography variant="body2">4.2万フォロワー</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Crown size={16} />
                                    <Typography variant="body2">156サブスクリプション</Typography>
                                </Box>
                                <Chip label="本人確認未提出" size="small" sx={{ backgroundColor: 'rgba(200,200,200,0.2)' }} />
                            </Box>
                            {/* QRコードボタン */}
                            <Button
                                startIcon={<QrCode style={{ width: 16, height: 16 }} />}
                                onClick={() => window.alert('QRコードを表示')}
                                variant="text"
                                sx={{ mt: 2, color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}
                            >
                                プロフィールのQRコードを表示
                            </Button>
                        </Box>

                        {/* アクションボタン */}
                        <ActionButtons>
                            <Button variant="contained" color="primary" >
                                フォロー
                            </Button>
                            <IconButton sx={{ backgroundColor: 'rgba(255,255,255,0.1)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}>
                                <MessageCircle size={20} style={{ color: 'white' }} />
                            </IconButton>
                        </ActionButtons>
                    </Box>
                </Box>
            </HeroContainer>

            {/* メインコンテンツ */}
            <Box sx={{
                maxWidth: '1280px',
                margin: '0 auto',
                padding: (theme) => theme.spacing(0, 4),
                mt: -12,
            }}>
                <Paper sx={{ backgroundColor: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(10px)', borderRadius: 3, boxShadow: 5, p: 4 }}>

                    <Tabs value={value} onChange={handleChange} aria-label="user profile tabs"
                        sx={{
                            marginBottom: 4,
                            '& .MuiTabs-indicator': {
                                backgroundColor: 'primary.main'
                            },
                            '& .MuiTab-root': {
                                color: (theme) => theme.palette.text.primary,
                                '&.Mui-selected': {
                                    color: (theme) => theme.palette.primary.contrastText,
                                    backgroundColor: (theme) => theme.palette.primary.main,
                                },
                            },
                        }}
                    >
                        <Tab label={<><ImageIcon style={{ width: 16, height: 16, marginRight: 8 }} />ギャラリー</>} {...a11yProps(0)} />
                        <Tab label={<><Star style={{ width: 16, height: 16, marginRight: 8 }} />おすすめ</>} {...a11yProps(1)} />
                        <Tab label={<><Palette style={{ width: 16, height: 16, marginRight: 8 }} />キャラクター</>} {...a11yProps(2)} />
                        <Tab label={<><BookOpen style={{ width: 16, height: 16, marginRight: 8 }} />シリーズ</>} {...a11yProps(3)} />
                        <Tab label={<><Users2 style={{ width: 16, height: 16, marginRight: 8 }} />交流</>} {...a11yProps(4)} />
                        <Tab label={<><Crown style={{ width: 16, height: 16, marginRight: 8 }} />プラン</>} {...a11yProps(5)} />
                        <Tab label={<><Users style={{ width: 16, height: 16, marginRight: 8 }} />統計</>} {...a11yProps(6)} />
                    </Tabs>

                    {/* ギャラリータブ */}
                    <TabPanel value={value} index={0}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 4 }}>
                            <FormControl sx={{ minWidth: 120 }}>
                                <Select defaultValue="post_desc">
                                    <MenuItem value="post_desc">投稿日新しい順</MenuItem>
                                    <MenuItem value="post_asc">投稿日古い順</MenuItem>
                                    <MenuItem value="favorite_desc">いいね多い順</MenuItem>
                                    <MenuItem value="pv_desc">閲覧数多い順</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControlLabel control={<Checkbox />} label="単品販売" />
                            <FormControlLabel control={<Checkbox />} label="メンバーシップ" />
                        </Box>
                        <ImageList sx={{ gridAutoRows: 200 }} cols={{ xs: 2, md: 3, lg: 4 }} gap={8}>
                            {[...Array(8)].map((_, i) => (
                                <ImageListItem key={i} sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    '&:hover': {
                                        boxShadow: 4,
                                        '& .overlay': {
                                            opacity: 1,
                                        }
                                    }
                                }}>
                                    <img
                                        src={`/api/placeholder/${300 + i}/${300 + i}`}
                                        alt={`作品 ${i + 1}`}
                                        style={{
                                            width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s',
                                            '&:hover': {
                                                transform: 'scale(1.1)'
                                            }
                                        }}
                                    />
                                    <Box className="overlay" sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 80%)',
                                        opacity: 0,
                                        transition: 'opacity 0.3s',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-end',
                                        padding: 2,
                                    }}>
                                        <Typography variant="h6" fontWeight="medium" color="white">作品 {i + 1}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.8)' }}>
                                            <Heart size={14} />
                                            <Typography variant="body2">1.2k</Typography>
                                        </Box>
                                    </Box>
                                </ImageListItem>
                            ))}
                        </ImageList>
                    </TabPanel>

                    {/* おすすめタブ */}
                    <TabPanel value={value} index={1}>
                        <Card sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                            <CardHeader title={<Typography variant="h6">おすすめ作品</Typography>} />
                            <CardContent>
                                <ImageList cols={{ xs: 2, md: 3, lg: 4 }} gap={8}>
                                    {[...Array(4)].map((_, i) => (
                                        <ImageListItem key={i} sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            borderRadius: 2,
                                        }}>
                                            <img
                                                src={`/api/placeholder/${400 + i}/${400 + i}`}
                                                alt={`おすすめ ${i + 1}`}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </ImageListItem>
                                    ))}
                                </ImageList>
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* キャラクタータブ */}
                    <TabPanel value={value} index={2}>
                        <Card sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                            <CardHeader title={<Typography variant="h6">オリジナルキャラクター</Typography>} />
                            <CardContent>
                                <Grid container spacing={3}>
                                    {[...Array(3)].map((_, i) => (
                                        <Grid item xs={12} md={6} lg={4} key={i}>
                                            <Box sx={{ display: 'flex', gap: 4, p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                                                <Avatar src={`/api/placeholder/200/200`} alt={`キャラクター ${i + 1}`} sx={{ width: 96, height: 96 }} variant="rounded" />
                                                <Box>
                                                    <Typography fontWeight="bold">キャラクター名</Typography>
                                                    <Typography variant="body2" color="grey.600">キャラクター設定の説明文がここに入ります。</Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* シリーズタブ */}
                    <TabPanel value={value} index={3}>
                        <Card sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                            <CardHeader title={<Typography variant="h6">シリーズ作品</Typography>} />
                            <CardContent>
                                <Grid container spacing={3}>
                                    {[...Array(2)].map((_, i) => (
                                        <Grid item xs={12} md={6} key={i}>
                                            <Box sx={{ backgroundColor: 'white', borderRadius: 2, p: 2 }}>
                                                <img
                                                    src={`/api/placeholder/800/400`}
                                                    alt={`シリーズ ${i + 1}`}
                                                    style={{ width: '100%', height: 192, objectFit: 'cover', borderRadius: 2, marginBottom: 2 }}
                                                />
                                                <Typography fontWeight="bold" variant="h6" mb={1}>シリーズタイトル</Typography>
                                                <Typography color="grey.600">シリーズの説明文がここに入ります。</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* 交流タブ */}
                    <TabPanel value={value} index={4}>
                        <Card sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                            <CardHeader title={<Typography variant="h6">最近の投稿</Typography>} />
                            <CardContent>
                                <List>
                                    {[...Array(3)].map((_, i) => (
                                        <ListItem key={i} alignItems="flex-start" sx={{ backgroundColor: 'white', borderRadius: 2, mb: 1, p: 2 }}>
                                            <ListItemAvatar>
                                                <Avatar src="/api/placeholder/100/100" alt="ユーザー" />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                        <Typography fontWeight="medium">ユーザー名</Typography>
                                                        <Typography variant="body2" color="grey.500">2時間前</Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <React.Fragment>
                                                        <Typography color="grey.600" mb={1}>
                                                            投稿のテキストコンテンツがここに入ります。
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 4 }}>
                                                            <Button
                                                                startIcon={<Heart size={16} />}
                                                                size="small"
                                                                variant="text"
                                                                color="info"
                                                            >
                                                                123
                                                            </Button>
                                                            <Button
                                                                startIcon={<MessageCircle size={16} />}
                                                                size="small"
                                                                variant="text"
                                                                color="info"
                                                            >
                                                                45
                                                            </Button>
                                                        </Box>
                                                    </React.Fragment>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* プランタブ */}
                    <TabPanel value={value} index={5}>
                        <Card sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                            <CardHeader title={<Typography variant="h6">サポートプラン</Typography>} />
                            <CardContent>
                                <Grid container spacing={3}>
                                    {[
                                        { name: 'ベーシック', price: '500', color: 'primary' },
                                        { name: 'スタンダード', price: '1,000', color: 'secondary' },
                                        { name: 'プレミアム', price: '3,000', color: 'warning' }
                                    ].map((plan, i) => (
                                        <Grid item xs={12} md={4} key={i}>
                                            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                                                <Box sx={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, backgroundColor: `${plan.color}.main` }}>
                                                    <Crown style={{ width: 32, height: 32, color: 'white' }} />
                                                </Box>
                                                <Typography variant="h6" fontWeight="bold" mb={1}>{plan.name}</Typography>
                                                <Typography variant="h5" fontWeight="bold" mb={2}><Box component="span" fontSize={24}>{plan.price}</Box>円 <Box component="span" fontSize={12} fontWeight="normal" color="grey.600">/月</Box></Typography>
                                                <Button variant="contained" color="primary" fullWidth>
                                                    登録する
                                                </Button>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </TabPanel>
                    {/* 統計タブ */}
                    <TabPanel value={value} index={6}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                                    <CardHeader title={<Typography variant="h6" fontWeight="medium">アクティビティ概要</Typography>} />
                                    <CardContent>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {[
                                                { label: '総閲覧数', value: '210万', growth: '+12%' },
                                                { label: '平均閲覧時間', value: '4分32秒', growth: '+8%' },
                                                { label: 'いいね率', value: '8.9%', growth: '+3%' }
                                            ].map((stat, i) => (
                                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: 1 }}>
                                                    <Typography color="grey.600" >{stat.label}</Typography>
                                                    <Box textAlign="right">
                                                        <Typography variant="h6" fontWeight="bold">{stat.value}</Typography>
                                                        <Typography variant="body2" color="success.main">{stat.growth}</Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card sx={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                                    <CardHeader title={<Typography variant="h6" fontWeight="medium">最近のアクティビティ</Typography>} />
                                    <CardContent>
                                        <List>
                                            {[...Array(4)].map((_, i) => (
                                                <ListItem key={i} sx={{
                                                    backgroundColor: 'rgba(255,255,255,0.5)',
                                                    borderRadius: 1,
                                                    mb: 1
                                                }}>
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ backgroundColor: 'rgba(100,150,255,0.1)' }}>
                                                            <Heart style={{ color: 'primary.main' }} />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={<Typography variant="body2" fontWeight="medium">新しい作品にいいねされました</Typography>}
                                                        secondary={<Typography variant="caption" color="grey.500">2時間前</Typography>}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </Paper>
            </Box>
        </Box>
    );
};

export default UserProfile2;